import cx from 'classnames';
import { Surface } from '@dx-ui/global-ui-prism';

import { useHeaderContext } from './context/header-context';
import { useHeaderStateContext } from './context/header-state-context';

import { HEADER_VARIANT_TYPE } from './header.constants';

import { MobileTopSection } from './composable/mobile/top-section';
import { MobileBottomSection } from './composable/mobile/bottom-section';
import { MobileMenuDrawerTrigger } from './composable/mobile/menu-drawer-trigger';
import { DrawerOverlay } from './composable/mobile/drawer-overlay';
import { MobileMenuDrawer } from './composable/mobile/menu-drawer';
import { MobileAccountSidebar } from './composable/mobile/account-sidebar';

import { MobileAccountSidebarItems } from './composable/mobile/account-sidebar-items';
import { LanguageSelector } from '@dx-ui/osc-language-selector';
import { MobileBottomFindYourStayLink } from './composable/mobile/bottom-find-your-stay-link';
import { HeaderLogoLink } from './header.logo';
import { MobileAuthCluster } from './composable/mobile/auth-cluster';
import { MobileNavDrawerList } from './composable/mobile/nav-drawer-list';
import { DesktopContainer } from './composable/desktop/container';
import { DesktopTopSection } from './composable/desktop/top-section';
import { DesktopLogo } from './composable/desktop/logo';

import { UserNavContainer } from './composable/desktop/user-nav/user-nav';
import { SignedInUserMenu } from './composable/desktop/user-nav/signedin-user-menu';
import { SignedOutAuthCluster } from './composable/desktop/user-nav/signed-out-auth-cluster';
import { FindStayNavigationLink } from './composable/desktop/user-nav/find-stay-navigation-link';

import { MobileContainer } from './composable/mobile/container';
import { DesktopMainNav } from './composable/desktop/main-nav';
import { DesktopBottomSection } from './composable/desktop/bottom-section';

export const DefaultHeaderVariant = ({ surfaceClassName }: { surfaceClassName?: string }) => {
  const {
    brand,
    user: { user },
    visual: { surfaceType, isTranslucent, suppressLogo, heading, isReimagineThemeExperience },
    navigation: { menuItems, languageSelectorOptions, headerVariantType },
  } = useHeaderContext();
  const {
    menuState: { isAccountOpen },
    displayFlags: { displayDesktopHeaderTopLeftLogo },
    styling: { logoVariant },
  } = useHeaderStateContext();

  return (
    <Surface
      type={surfaceType || 'base'}
      className={cx('osc-header-wrapper', surfaceClassName, {
        '!bg-translucent': isTranslucent && !isReimagineThemeExperience,
        '!bg-transparent': isTranslucent && isReimagineThemeExperience,
      })}
    >
      <MobileContainer>
        <MobileTopSection>
          {!!languageSelectorOptions && (
            <div className="ms-auto pe-1 pt-2 md:pe-7">
              <LanguageSelector {...languageSelectorOptions} />
            </div>
          )}
        </MobileTopSection>

        <MobileBottomSection
          leadingChildClassname="flex"
          leadingChild={
            <>
              <MobileMenuDrawerTrigger />
              {!suppressLogo ? (
                <HeaderLogoLink
                  variant={logoVariant}
                  brand={brand}
                  className={cx({
                    'self-end sm:self-center': !!languageSelectorOptions,
                  })}
                  heading={heading}
                />
              ) : null}
            </>
          }
          trailingChild={
            <>
              <MobileBottomFindYourStayLink />
              <MobileAuthCluster />
            </>
          }
        />
        <DrawerOverlay />
        <MobileMenuDrawer className="left-0">
          <MobileNavDrawerList />
        </MobileMenuDrawer>
        <MobileAccountSidebar>
          {user && isAccountOpen ? <MobileAccountSidebarItems /> : null}
        </MobileAccountSidebar>
      </MobileContainer>

      <DesktopContainer>
        <DesktopTopSection
          leadingChild={!!displayDesktopHeaderTopLeftLogo && <DesktopLogo />}
          trailingChild={
            <UserNavContainer>
              <FindStayNavigationLink />
              {user ? <SignedInUserMenu /> : <SignedOutAuthCluster />}
            </UserNavContainer>
          }
        />

        <DesktopBottomSection>
          {!!menuItems?.length && headerVariantType !== HEADER_VARIANT_TYPE.minimal && (
            <DesktopMainNav mainNavLinks={menuItems} />
          )}
        </DesktopBottomSection>
      </DesktopContainer>
    </Surface>
  );
};
